// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { DateInfoRow } from '@/components/date-info-row';
import { LocationPairCell } from '@/components/location-pair-cell';
import { StatusChip } from '@/components/status-chip';
import {
  Inventory2Outlined,
  SettingsOutlined,
} from '@/constants/hardfin-standard-icons';
import { PurchaseOrder } from '@/purchase-order/api/types';
import {
  PHASE_CONFIG,
  PO_STATUS_TOOLTIPS,
  getModifierVariant,
  isCancelled,
  parseStatus,
} from '@/purchase-order/constants/status';
import { getStatusIcon } from '@/purchase-order/constants/status-icons';
import { formatNumber } from '@/utilities/number';
import { Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { MouseEvent } from 'react';

/**
 * Props for the PurchaseOrderTableRow component.
 */
interface Props {
  purchaseOrder: PurchaseOrder;
}

/**
 * Truncates text to a maximum number of lines, intelligently handling line breaks.
 * Returns the truncated text and whether truncation was needed.
 *
 * @param text - The text to potentially truncate
 * @param maxLines - Maximum number of lines to display (default: 2)
 * @param maxCharsPerLine - Approximate max characters per line (default: 40)
 * @returns Object with truncated text and whether truncation occurred
 */
function truncateToLines(
  text: string,
  maxLines = 2,
  maxCharsPerLine = 40
): { text: string; isTruncated: boolean } {
  const maxTotalChars = maxLines * maxCharsPerLine;

  // Split by actual line breaks
  const lines = text.split('\n');

  // If we have more actual line breaks than allowed, take first N lines
  let workingText = text;
  let wasTruncatedByLines = false;
  if (lines.length > maxLines) {
    workingText = lines.slice(0, maxLines).join('\n');
    wasTruncatedByLines = true;
  }

  // Always enforce character limit
  if (workingText.length > maxTotalChars) {
    // Try to truncate at a word boundary
    let truncatedText = workingText.substring(0, maxTotalChars);
    const lastSpaceIndex = truncatedText.lastIndexOf(' ');

    // If we found a space and it's not too far back, truncate there
    if (lastSpaceIndex > maxTotalChars * 0.8) {
      truncatedText = truncatedText.substring(0, lastSpaceIndex);
    }

    return {
      text: truncatedText + '...',
      isTruncated: true,
    };
  }

  // If we truncated by lines but not by chars, still need ellipsis
  if (wasTruncatedByLines) {
    return {
      text: workingText + '...',
      isTruncated: true,
    };
  }

  // No truncation needed
  return {
    text,
    isTruncated: false,
  };
}

/**
 * Creates tooltip content showing all purchase order dates
 */
function getAllPurchaseOrderDatesContent(purchaseOrder: PurchaseOrder) {
  const { phase, modifier } = parseStatus(purchaseOrder.status);
  const isFullyReceived =
    phase === 'received' && (modifier === 'full' || modifier === 'overage');

  const dates = [
    {
      label: 'Created:',
      date:
        purchaseOrder.createdAt?.toLocaleString({ dateStyle: 'medium' }) ||
        null,
    },
    {
      label: 'Promise ship:',
      date:
        purchaseOrder.promiseShipDate?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    {
      label: 'Promise delivery:',
      date:
        purchaseOrder.promiseDeliveryDate?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    ...(purchaseOrder.mostRecentShippedAt
      ? [
          {
            label: isFullyReceived ? 'Shipped:' : 'Most recent ship:',
            date: purchaseOrder.mostRecentShippedAt.toLocaleString({
              dateStyle: 'medium',
            }),
          },
        ]
      : []),
    ...(purchaseOrder.mostRecentDeliveredAt
      ? [
          {
            label: isFullyReceived ? 'Delivered:' : 'Most recent deliver:',
            date: purchaseOrder.mostRecentDeliveredAt.toLocaleString({
              dateStyle: 'medium',
            }),
          },
        ]
      : []),
    ...(purchaseOrder.mostRecentReceivedAt
      ? [
          {
            label: isFullyReceived ? 'Received:' : 'Most recent receive:',
            date: purchaseOrder.mostRecentReceivedAt.toLocaleString({
              dateStyle: 'medium',
            }),
          },
        ]
      : []),
    ...(purchaseOrder.cancelledAt
      ? [
          {
            label: 'Cancelled:',
            date:
              purchaseOrder.cancelledAt?.toLocaleString({
                dateStyle: 'medium',
              }) || null,
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={0.25}>
      {dates.map(({ label, date }) => (
        <DateInfoRow key={label} label={label} date={date} />
      ))}
    </Stack>
  );
}

/**
 * Gets the most relevant date for a purchase order based on its status.
 * Uses cascading priority rules based on PO lifecycle phase.
 */
function getRelevantDate(purchaseOrder: PurchaseOrder) {
  const { phase, modifier } = parseStatus(purchaseOrder.status);

  // 1. Cancelled - show cancelled date
  if (isCancelled(purchaseOrder.status) && purchaseOrder.cancelledAt) {
    return {
      date: purchaseOrder.cancelledAt.toLocaleString({ dateStyle: 'medium' }),
      label: null,
    };
  }

  // 2. Fully/Over Received - show latest received date
  if (phase === 'received' && (modifier === 'full' || modifier === 'overage')) {
    if (purchaseOrder.mostRecentReceivedAt) {
      return {
        date: purchaseOrder.mostRecentReceivedAt.toLocaleString({
          dateStyle: 'medium',
        }),
        label: null,
      };
    }
  }

  // 3. Partial Received - show promise delivery or promise ship
  if (phase === 'received' && modifier === 'partial') {
    if (purchaseOrder.promiseDeliveryDate) {
      return {
        date: purchaseOrder.promiseDeliveryDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise delivery',
      };
    }
    if (purchaseOrder.promiseShipDate) {
      return {
        date: purchaseOrder.promiseShipDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise ship',
      };
    }
  }

  // 4. Fully/Over Delivered - show latest delivered date
  if (
    phase === 'delivered' &&
    (modifier === 'full' || modifier === 'overage')
  ) {
    if (purchaseOrder.mostRecentDeliveredAt) {
      return {
        date: purchaseOrder.mostRecentDeliveredAt.toLocaleString({
          dateStyle: 'medium',
        }),
        label: null,
      };
    }
  }

  // 5. Partial Delivered - show promise delivery or promise ship
  if (phase === 'delivered' && modifier === 'partial') {
    if (purchaseOrder.promiseDeliveryDate) {
      return {
        date: purchaseOrder.promiseDeliveryDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise delivery',
      };
    }
    if (purchaseOrder.promiseShipDate) {
      return {
        date: purchaseOrder.promiseShipDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise ship',
      };
    }
  }

  // 6. Promise delivery date set (for open, awaiting_3pl, draft phases)
  if (purchaseOrder.promiseDeliveryDate) {
    return {
      date: purchaseOrder.promiseDeliveryDate.toLocaleString({
        dateStyle: 'medium',
      }),
      label: 'Promise delivery',
    };
  }

  // 7. Shipped phase - show latest shipped date
  if (phase === 'shipped' && purchaseOrder.mostRecentShippedAt) {
    return {
      date: purchaseOrder.mostRecentShippedAt.toLocaleString({
        dateStyle: 'medium',
      }),
      label: null,
    };
  }

  // 8. Promise ship date set
  if (purchaseOrder.promiseShipDate) {
    return {
      date: purchaseOrder.promiseShipDate.toLocaleString({
        dateStyle: 'medium',
      }),
      label: 'Promise ship',
    };
  }

  // 9. Updated != Created - show updated date
  if (
    purchaseOrder.lastUpdatedAt.toMillis() !==
    purchaseOrder.createdAt.toMillis()
  ) {
    return {
      date: purchaseOrder.lastUpdatedAt.toLocaleString({ dateStyle: 'medium' }),
      label: 'Updated',
    };
  }

  // 10. Created date (fallback)
  return {
    date: purchaseOrder.createdAt.toLocaleString({ dateStyle: 'medium' }),
    label: 'Created',
  };
}

/**
 * A table row component for displaying purchase order information.
 */
export function PurchaseOrderTableRow(props: Props) {
  const navigate = useNavigate();

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.metaKey || event.ctrlKey) {
      window.open(`/purchase-orders/${props.purchaseOrder.key}`, '_blank');
    } else {
      navigate({
        to: '/purchase-orders/$purchaseOrderKey',
        params: { purchaseOrderKey: props.purchaseOrder.key },
      });
    }
  };

  return (
    <TableRow onClick={handleRowClick} hover sx={{ cursor: 'pointer' }}>
      {/* References Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Typography variant="subtitle2">
          {props.purchaseOrder.purchaseOrderNumber}
        </Typography>
        {props.purchaseOrder.externalPurchaseOrderNumber && (
          <Stack
            direction="row"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={0.3}
          >
            <Typography variant="subtitle2">External PO:</Typography>
            <Typography variant="body2">
              {props.purchaseOrder.externalPurchaseOrderNumber}
            </Typography>
          </Stack>
        )}
        {props.purchaseOrder.supplier && (
          <Stack
            direction="row"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={0.3}
          >
            <Typography variant="subtitle2">
              {props.purchaseOrder.supplier.isSupplier
                ? 'Supplier'
                : 'Customer'}
              :
            </Typography>
            <Typography variant="body2">
              {props.purchaseOrder.supplier.name}
            </Typography>
          </Stack>
        )}
        {props.purchaseOrder.orderNotes &&
          (() => {
            const { text: truncatedText, isTruncated } = truncateToLines(
              props.purchaseOrder.orderNotes,
              2,
              40
            );
            return (
              <Stack
                direction="row"
                alignItems="flex-start"
                flexWrap="wrap"
                gap={0.3}
              >
                <Typography variant="subtitle2">Note:</Typography>
                <ConditionalTooltip
                  arrow
                  tooltipText={
                    isTruncated ? (
                      <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                        {props.purchaseOrder.orderNotes}
                      </Typography>
                    ) : undefined
                  }
                  placement="top"
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {truncatedText}
                  </Typography>
                </ConditionalTooltip>
              </Stack>
            );
          })()}
      </TableCell>

      {/* From/To Location Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <LocationPairCell
          fromLocation={props.purchaseOrder.fromLocation}
          toLocation={props.purchaseOrder.toLocation}
          showThirdPartyChip
        />
      </TableCell>

      {/* Items Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack spacing={0.3}>
          {props.purchaseOrder.items
            .slice(0, props.purchaseOrder.items.length > 4 ? 3 : 4)
            .map((item) => {
              const ItemIcon = !item.item.hasAssets
                ? SettingsOutlined
                : Inventory2Outlined;
              return (
                <Stack
                  key={item.id}
                  direction="row"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={0.3}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.5}
                  >
                    <ItemIcon sx={{ fontSize: '1rem' }} />
                    <>
                      <Typography variant="body2">{item.item.sku}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({item.item.name})
                      </Typography>
                    </>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      &times; {formatNumber(item.requestedQuantity)}
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
          {props.purchaseOrder.items.length > 4 && (
            <Tooltip
              arrow
              title={
                <Stack spacing={0.5}>
                  {props.purchaseOrder.items.slice(3).map((item) => {
                    const itemLabel = `${item.item.sku} (${item.item.name})`;
                    return (
                      <Typography key={item.id} variant="caption">
                        {itemLabel} &times;{' '}
                        {formatNumber(item.requestedQuantity)}
                      </Typography>
                    );
                  })}
                </Stack>
              }
            >
              <Typography variant="body2" color="text.secondary">
                +{props.purchaseOrder.items.length - 3} more
              </Typography>
            </Tooltip>
          )}
        </Stack>
      </TableCell>

      {/* Status Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          {/* Status chip and relevant date */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            flexWrap="wrap"
            width="100%"
          >
            {(() => {
              const { phase, modifier } = parseStatus(
                props.purchaseOrder.status
              );
              const config = PHASE_CONFIG[phase];
              return (
                <Tooltip
                  title={PO_STATUS_TOOLTIPS[props.purchaseOrder.status]}
                  arrow
                >
                  <StatusChip
                    icon={getStatusIcon(phase, modifier)}
                    label={config.label}
                    color={config.chipConfig.color}
                    variant={getModifierVariant(phase, modifier)}
                  />
                </Tooltip>
              );
            })()}
            {(() => {
              const relevantDate = getRelevantDate(props.purchaseOrder);
              if (relevantDate.date) {
                return (
                  <Tooltip
                    arrow
                    placement="left"
                    title={getAllPurchaseOrderDatesContent(props.purchaseOrder)}
                  >
                    <Stack alignItems="flex-end">
                      <Typography variant="body2" color="text.secondary">
                        {relevantDate.label && `${relevantDate.label}`}
                      </Typography>
                      <Typography variant="body2">
                        {relevantDate.date}
                      </Typography>
                    </Stack>
                  </Tooltip>
                );
              }
              return (
                <Tooltip
                  arrow
                  placement="left"
                  title={getAllPurchaseOrderDatesContent(props.purchaseOrder)}
                >
                  <Typography variant="body2" color="text.secondary">
                    No date set
                  </Typography>
                </Tooltip>
              );
            })()}
          </Stack>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
